// 📄 components/shared/RestriccionesTagEditor.tsx
// Configuración de "restricciones por tag" de una plantilla del Validador
// de Estructura — reglas técnicas (longitud, solo numérico, requerido,
// valores permitidos) independientes de la comparación de estructura
// contra el XML de referencia. Compartido entre PlantillaGlobalFormModal.tsx
// (Admin) y EditarPlantillaProyectoModal.tsx (Configuración del proyecto) —
// mismo componente controlado en los dos, no se duplica el formulario.
import { useMemo, useState } from "react";
import { Search, Plus, ChevronDown, ChevronUp, Trash2, Info } from "lucide-react";
import { parseXmlString, extraerTagsUnicos, RestriccionTag, RestriccionReglas } from "../../utils/xmlStructureUtils.ts";

interface RestriccionesTagEditorProps {
  // XML de referencia de la plantilla — de acá se extraen los tags
  // configurables. Vacío (plantilla sin archivo todavía) deshabilita la
  // sección con un mensaje, en vez de mostrar una lista vacía confusa.
  contenidoXML: string;
  restricciones: RestriccionTag[];
  onChange: (restricciones: RestriccionTag[]) => void;
}

const tieneAlgunaActiva = (r: RestriccionReglas): boolean =>
  typeof r.longitudMaxima === "number" ||
  typeof r.longitudMinima === "number" ||
  !!r.soloNumerico ||
  !!r.requerido ||
  !!(r.valoresPermitidos && r.valoresPermitidos.length > 0);

const inputClass = "w-full bg-zinc-800 border border-white/10 rounded-lg p-2 text-xs text-white focus:outline-none focus:border-indigo-500";
const miniLabelClass = "block text-[10px] text-gray-500 mb-1";

export default function RestriccionesTagEditor({ contenidoXML, restricciones, onChange }: RestriccionesTagEditorProps) {
  const [busqueda, setBusqueda] = useState("");
  const [expandidos, setExpandidos] = useState<Set<string>>(new Set());
  // Texto crudo de "valores permitidos" por tag mientras se escribe — igual
  // criterio que tagsInput en otras secciones del proyecto: separar el
  // texto en edición del array ya parseado evita que un "," o espacio a
  // medio escribir se pierda en cada re-render.
  const [valoresPermitidosInputs, setValoresPermitidosInputs] = useState<Record<string, string>>({});

  const { tags, errorParseo } = useMemo(() => {
    if (!contenidoXML.trim()) return { tags: [] as string[], errorParseo: null as string | null };
    try {
      return { tags: extraerTagsUnicos(parseXmlString(contenidoXML)), errorParseo: null as string | null };
    } catch (err: any) {
      return { tags: [] as string[], errorParseo: err.message || "No se pudo leer el XML para extraer los tags." };
    }
  }, [contenidoXML]);

  const tagsFiltrados = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    if (!q) return tags;
    return tags.filter((t) => t.toLowerCase().includes(q));
  }, [tags, busqueda]);

  const restriccionPorTag = useMemo(() => {
    const mapa = new Map<string, RestriccionTag>();
    restricciones.forEach((r) => mapa.set(r.tag, r));
    return mapa;
  }, [restricciones]);

  const toggleExpandido = (tag: string) => {
    setExpandidos((prev) => {
      const next = new Set(prev);
      if (next.has(tag)) {
        next.delete(tag);
      } else {
        next.add(tag);
        const existente = restriccionPorTag.get(tag);
        setValoresPermitidosInputs((inputs) => ({ ...inputs, [tag]: (existente?.restricciones.valoresPermitidos || []).join(", ") }));
      }
      return next;
    });
  };

  // Los tags sin ninguna restricción activa no se guardan (ver Parte 2 del
  // pedido) — si el último campo activo se limpia, la entrada desaparece
  // del array en vez de quedar como un objeto vacío.
  const actualizarCampo = (tag: string, patchReglas: Partial<RestriccionReglas>, patchDescripcion?: string) => {
    const existente = restriccionPorTag.get(tag);
    const nuevasReglas: RestriccionReglas = { ...(existente?.restricciones || {}), ...patchReglas };
    const nuevaDescripcion = patchDescripcion !== undefined ? patchDescripcion : existente?.descripcion;
    const sinTag = restricciones.filter((r) => r.tag !== tag);

    if (!tieneAlgunaActiva(nuevasReglas)) {
      onChange(sinTag);
      return;
    }

    const actualizado: RestriccionTag = {
      tag,
      restricciones: nuevasReglas,
      ...(nuevaDescripcion && nuevaDescripcion.trim() ? { descripcion: nuevaDescripcion.trim() } : {})
    };
    onChange([...sinTag, actualizado]);
  };

  const quitarRestriccion = (tag: string) => {
    onChange(restricciones.filter((r) => r.tag !== tag));
    setValoresPermitidosInputs((inputs) => ({ ...inputs, [tag]: "" }));
  };

  const handleValoresPermitidosInput = (tag: string, raw: string) => {
    setValoresPermitidosInputs((prev) => ({ ...prev, [tag]: raw }));
    const lista = raw.split(",").map((v) => v.trim()).filter(Boolean);
    actualizarCampo(tag, { valoresPermitidos: lista.length > 0 ? lista : undefined });
  };

  return (
    <div className="space-y-3">
      <div className="bg-indigo-500/10 border border-indigo-500/20 text-indigo-300 p-3 rounded-lg text-xs flex items-start gap-2">
        <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <span>
          Reglas técnicas independientes de la comparación de estructura: validan el documento del usuario aunque su
          valor no coincida con el de esta plantilla (ej. longitud máxima, solo numérico, valores permitidos).
        </span>
      </div>

      {!contenidoXML.trim() ? (
        <p className="text-xs text-gray-500">Carga un archivo XML para poder configurar restricciones por campo.</p>
      ) : errorParseo ? (
        <p className="text-xs text-red-400">{errorParseo}</p>
      ) : tags.length === 0 ? (
        <p className="text-xs text-gray-500">El XML no tiene campos para configurar.</p>
      ) : (
        <>
          <div className="relative">
            <Search className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder="Buscar tag..."
              className={`${inputClass} pl-9`}
            />
          </div>

          <div className="max-h-96 overflow-y-auto space-y-1.5 pr-1">
            {tagsFiltrados.length === 0 ? (
              <p className="text-xs text-gray-500 py-2">Ningún tag coincide con la búsqueda.</p>
            ) : (
              tagsFiltrados.map((tag) => {
                const restriccion = restriccionPorTag.get(tag);
                const configurada = !!restriccion;
                const abierto = expandidos.has(tag);
                const r = restriccion?.restricciones || {};

                return (
                  <div key={tag} className={`border rounded-lg overflow-hidden ${configurada ? "border-indigo-500/30 bg-indigo-500/5" : "border-white/10 bg-white/5"}`}>
                    <button
                      type="button"
                      onClick={() => toggleExpandido(tag)}
                      className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left hover:bg-white/5 transition-colors"
                    >
                      <span className="text-xs font-mono text-gray-300 truncate" title={tag}>{tag}</span>
                      <span className="flex items-center gap-2 flex-shrink-0">
                        {configurada && (
                          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-indigo-500/20 text-indigo-300 border border-indigo-500/30">Configurada</span>
                        )}
                        <span className="text-[11px] text-indigo-400 font-semibold flex items-center gap-1">
                          {abierto ? <ChevronUp className="w-3.5 h-3.5" /> : configurada ? <ChevronDown className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
                          {configurada ? (abierto ? "Ocultar" : "Editar") : "Agregar restricción"}
                        </span>
                      </span>
                    </button>

                    {abierto && (
                      <div className="p-3 border-t border-white/10 space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className={miniLabelClass}>Longitud máxima</label>
                            <input
                              type="number"
                              min={0}
                              className={inputClass}
                              value={r.longitudMaxima ?? ""}
                              onChange={(e) => actualizarCampo(tag, { longitudMaxima: e.target.value === "" ? undefined : Math.max(0, Number(e.target.value)) })}
                            />
                          </div>
                          <div>
                            <label className={miniLabelClass}>Longitud mínima</label>
                            <input
                              type="number"
                              min={0}
                              className={inputClass}
                              value={r.longitudMinima ?? ""}
                              onChange={(e) => actualizarCampo(tag, { longitudMinima: e.target.value === "" ? undefined : Math.max(0, Number(e.target.value)) })}
                            />
                          </div>
                        </div>

                        <div className="flex items-center gap-4">
                          <label className="flex items-center gap-1.5 text-xs text-gray-300">
                            <input
                              type="checkbox"
                              checked={!!r.soloNumerico}
                              onChange={(e) => actualizarCampo(tag, { soloNumerico: e.target.checked || undefined })}
                              className="accent-indigo-500 w-3.5 h-3.5"
                            />
                            Solo numérico
                          </label>
                          <label className="flex items-center gap-1.5 text-xs text-gray-300">
                            <input
                              type="checkbox"
                              checked={!!r.requerido}
                              onChange={(e) => actualizarCampo(tag, { requerido: e.target.checked || undefined })}
                              className="accent-indigo-500 w-3.5 h-3.5"
                            />
                            Requerido
                          </label>
                        </div>

                        <div>
                          <label className={miniLabelClass}>Valores permitidos (separados por coma)</label>
                          <input
                            type="text"
                            className={inputClass}
                            value={valoresPermitidosInputs[tag] ?? (r.valoresPermitidos || []).join(", ")}
                            onChange={(e) => handleValoresPermitidosInput(tag, e.target.value)}
                            placeholder="ej. S, N"
                          />
                        </div>

                        <div>
                          <label className={miniLabelClass}>Descripción / nota (opcional)</label>
                          <input
                            type="text"
                            className={inputClass}
                            value={restriccion?.descripcion || ""}
                            onChange={(e) => actualizarCampo(tag, {}, e.target.value)}
                            placeholder='ej. "Máx 4 chars según customizing ERP tabla T001"'
                          />
                        </div>

                        {configurada && (
                          <button
                            type="button"
                            onClick={() => quitarRestriccion(tag)}
                            className="flex items-center gap-1.5 text-[11px] text-red-400 hover:text-red-300"
                          >
                            <Trash2 className="w-3.5 h-3.5" /> Quitar restricción de este tag
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </>
      )}
    </div>
  );
}
